import React, { useEffect, useState, useCallback } from 'react';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onRemove: (id: string) => void;
}

/** Duration of the slide-out animation; must match .toast-exiting in App.css */
const EXIT_ANIMATION_MS = 200;

const TOAST_ICONS: Record<ToastMessage['type'], string> = {
  success: '✅',
  warning: '⚠️',
  error: '❌',
  info: 'ℹ️',
};

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  const dismiss = useCallback(() => {
    setIsExiting(true);
  }, []);

  // Auto-dismiss after the configured duration
  useEffect(() => {
    const timer = setTimeout(dismiss, toast.duration || 3000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  // Once the exit animation starts, remove the toast when it finishes
  useEffect(() => {
    if (!isExiting) return;
    const timer = setTimeout(() => onRemove(toast.id), EXIT_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isExiting, toast.id, onRemove]);

  const getToastClass = () => {
    switch (toast.type) {
      case 'success':
        return 'toast-success';
      case 'warning':
        return 'toast-warning';
      case 'error':
        return 'toast-error';
      default:
        return 'toast-info';
    }
  };

  return (
    <div className={`toast ${getToastClass()} ${isExiting ? 'toast-exiting' : ''}`}>
      <span className="toast-icon" aria-hidden="true">{TOAST_ICONS[toast.type]}</span>
      <span className="toast-message">{toast.message}</span>
      <button
        className="toast-close"
        onClick={dismiss}
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastMessage[];
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

export default Toast;
